// AGC: Machine Instance Types for DroidFarm
// AGC: Extends the static machine catalog with actual machine instances
// AGC: Tracks operational data, maintenance, and usage logs

import type { MachineCategory } from './machine'

/**
 * AGC: Machine status types
 */
// AGC: 'sold' status added — when a machine is sold via revenue entry, it gets this status
export type MachineStatus = 'available' | 'in_use' | 'maintenance' | 'broken' | 'retired' | 'sold'

/**
 * AGC: Fuel type for machines
 */
export type FuelType = 'diesel' | 'gasoline' | 'electric' | 'none'

/**
 * AGC: Machine specifications (optional, varies by category)
 */
export interface MachineSpecs {
  // AGC: Sprayer specific
  boomLengthMeters?: number
  tankCapacityLiters?: number
  nozzleCount?: number
  nozzleType?: string
  
  // AGC: Tractor specific
  horsePower?: number
  ptoType?: string
  ptoHorsePower?: number
  
  // AGC: Harvester specific
  headerWidthMeters?: number
  grainTankCapacity?: number
  
  // AGC: Planter specific
  numberOfLines?: number

  // AGC: General specs
  workingWidthMeters?: number
  capacity?: number
  weight?: number
}

/**
 * AGC: Machine instance - represents a physical machine owned by the farm
 * AGC: Tracks operational data, costs, maintenance schedules
 */
export interface MachineInstance {
  id: string
  userId: string           // AGC: Owner's Firebase UID
  farmId?: string          // AGC: Farm this machine belongs to (for multi-farm support)
  
  // AGC: Basic Information
  name: string             // AGC: Custom name (e.g., "Trator John Deere 6130R")
  category: MachineCategory
  catalogId?: string       // AGC: Optional link to FARM_MACHINES catalog
  brand?: string           // AGC: Manufacturer (e.g., "John Deere")
  model?: string           // AGC: Model number (e.g., "6130R")
  year?: number            // AGC: Year of manufacture
  serialNumber?: string    // AGC: Serial/chassis number
  
  subtype?: string         // AGC: Machine subtype (e.g., 'trator_medio', 'pulverizador_barras')
  brandColor?: string      // AGC: Learned brand color from icon picker (for unknown brands)

  // AGC: Photo & icon
  photoUrl?: string        // AGC: Custom photo URL or null for default
  customIconPath?: string  // AGC: User-picked icon path from icon picker
  
  // AGC: Operational Data
  horimeterHours: number   // AGC: Total operating hours (odometer equivalent)
  hectaresWorked: number   // AGC: Total hectares processed (for implements)
  
  // AGC: Cost Tracking (optional for MVP)
  acquisitionCost?: number
  acquisitionDate?: string // AGC: ISO date
  hourlyOperatingCost?: number
  fuelType?: FuelType
  fuelConsumptionPerHour?: number  // AGC: Liters per hour
  fuelTankCapacity?: number        // AGC: L, user-editable per machine
  fuelTrackingEnabled?: boolean    // AGC: Phase 2 opt-in flag
  // AGC: true = máquina autopropelida (self-propelled); false/undefined = towed / needs trator.
  // AGC: See docs/TASK_ACTIVITY_RULES.md for the selfPropelled convention and Phase 1 heuristic.
  selfPropelled?: boolean

  // AGC: Technical Specs (category-specific)
  specs?: MachineSpecs
  
  // AGC: Maintenance Management
  maintenanceIntervalHours?: number      // AGC: Service every X hours
  maintenanceIntervalHectares?: number   // AGC: Or every X hectares
  maintenanceIntervalDays?: number       // AGC: Or every X days
  lastMaintenanceDate?: string           // AGC: ISO date of last service
  lastMaintenanceHours?: number          // AGC: Horimeter reading at last service
  nextMaintenanceDue?: string            // AGC: Calculated next service date
  maintenanceNotes?: string              // AGC: General maintenance instructions
  activeMaintenanceIntervals?: string[]  // AGC: IDs of active standard maintenance intervals
  
  // AGC: Status
  status: MachineStatus
  isArchived: boolean      // AGC: Soft delete
  notes?: string           // AGC: General notes/description
  
  // AGC: Timestamps
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Machine usage log - records each time a machine is used
 * AGC: Created when a task involving this machine is completed
 */
export interface MachineUsageLog {
  id: string
  machineId: string        // AGC: Link to MachineInstance
  machineName: string      // AGC: Denormalized for display
  taskId?: string          // AGC: Associated task (optional)
  operatorId: string       // AGC: User who operated the machine
  operatorName: string     // AGC: Denormalized for display
  userId: string           // AGC: Farm owner (for filtering)
  
  // AGC: Location (optional)
  lotId?: string
  lotName?: string
  fieldId?: string
  fieldName?: string
  
  // AGC: Time tracking
  startTime: string        // AGC: ISO timestamp when started
  endTime: string          // AGC: ISO timestamp when finished
  hoursUsed: number        // AGC: Duration in hours
  
  // AGC: Work metrics
  hectaresWorked?: number  // AGC: Area processed (for implements/sprayers)
  fuelConsumed?: number    // AGC: Fuel used (liters)
  
  // AGC: Notes
  notes?: string           // AGC: Operator notes/observations
  
  // AGC: Timestamps
  createdAt: string
}

/**
 * AGC: Machine with calculated maintenance status
 * AGC: Used in UI to show maintenance alerts
 */
export interface MachineWithMaintenanceStatus extends MachineInstance {
  maintenanceStatus: 'ok' | 'due_soon' | 'overdue'
  maintenanceDaysUntilDue?: number
  maintenanceHoursUntilDue?: number
}

/**
 * AGC: Calculate if maintenance is due for a machine
 */
export const calculateMaintenanceStatus = (
  machine: MachineInstance
): MachineWithMaintenanceStatus => {
  let maintenanceStatus: 'ok' | 'due_soon' | 'overdue' = 'ok'
  let maintenanceDaysUntilDue: number | undefined
  let maintenanceHoursUntilDue: number | undefined

  // AGC: Check hours-based maintenance
  if (machine.maintenanceIntervalHours && machine.lastMaintenanceHours !== undefined) {
    const hoursSinceLastMaintenance = machine.horimeterHours - machine.lastMaintenanceHours
    const hoursUntilDue = machine.maintenanceIntervalHours - hoursSinceLastMaintenance
    maintenanceHoursUntilDue = hoursUntilDue

    if (hoursUntilDue <= 0) {
      maintenanceStatus = 'overdue'
    } else if (hoursUntilDue <= 10) {  // AGC: Due soon if within 10 hours
      maintenanceStatus = 'due_soon'
    }
  }

  // AGC: Check date-based maintenance
  if (machine.maintenanceIntervalDays && machine.lastMaintenanceDate) {
    const lastMaintenance = new Date(machine.lastMaintenanceDate)
    const today = new Date()
    const daysSinceLastMaintenance = Math.floor(
      (today.getTime() - lastMaintenance.getTime()) / (1000 * 60 * 60 * 24)
    )
    const daysUntilDue = machine.maintenanceIntervalDays - daysSinceLastMaintenance
    maintenanceDaysUntilDue = daysUntilDue

    if (daysUntilDue <= 0) {
      maintenanceStatus = 'overdue'
    } else if (daysUntilDue <= 7) {  // AGC: Due soon if within 7 days
      if (maintenanceStatus !== 'overdue') {
        maintenanceStatus = 'due_soon'
      }
    }
  }

  return {
    ...machine,
    maintenanceStatus,
    maintenanceDaysUntilDue,
    maintenanceHoursUntilDue,
  }
}

/**
 * AGC: Get default photo URL for a machine category
 * AGC: Returns placeholder image path based on category
 */
export const getDefaultMachinePhoto = (category: MachineCategory): string => {
  // AGC: Map to icon/placeholder - in real app would be actual images
  const defaults: Record<MachineCategory, string> = {
    trator: '/assets/machines/tractor.png',
    colheitadeira: '/assets/machines/harvester.png',
    plantadeira: '/assets/machines/planter.png',
    pulverizador: '/assets/machines/sprayer.png',
    implemento: '/assets/machines/implement.png',
    irrigacao: '/assets/machines/irrigation.png',
    transporte: '/assets/machines/transport.png',
    outros: '/assets/machines/other.png',
  }
  return defaults[category] || defaults.outros
}

/**
 * AGC: Format horimeter hours for display
 */
export const formatHorimeterHours = (hours: number): string => {
  return `${hours.toFixed(1)}h`
}

/**
 * AGC: Format hectares worked for display
 */
export const formatHectaresWorked = (hectares: number): string => {
  return `${hectares.toFixed(1)} ha`
}

/**
 * AGC: Get machine status badge color
 */
export const getMachineStatusColor = (status: MachineStatus): string => {
  const colors: Record<MachineStatus, string> = {
    available: 'bg-green-500',
    in_use: 'bg-blue-500',
    maintenance: 'bg-yellow-500',
    broken: 'bg-red-500',
    retired: 'bg-gray-500',
  }
  return colors[status]
}

/**
 * AGC: Get machine status label (Portuguese)
 */
export const getMachineStatusLabel = (status: MachineStatus): string => {
  const labels: Record<MachineStatus, string> = {
    available: 'Disponível',
    in_use: 'Em Uso',
    maintenance: 'Manutenção',
    broken: 'Quebrado',
    retired: 'Desativado',
  }
  return labels[status]
}
